// 5:7-aspect invitation card preview. Pure presentational component.
// Two render paths:
//   • template.cardImage  — full-bleed webp art with the names + date +
//                            venue overlaid in the lower portion. Text
//                            uses the template's own font/ink/accent so
//                            it reads as part of the same design system.
//   • else                — falls through to the procedural LAYOUTS
//                            dispatcher (the original behaviour).
//
// Text scaling: the shell sets `container-type: size`, and the overlay
// uses `cqw` units, so a single 5:7 card looks right at every render
// size — tiny landing thumbnail, mobile gallery tile, full desktop
// detail card.

import type { Template } from "@/lib/data";
import { LAYOUTS, type LayoutCtx } from "./layouts";

interface InvitationCardProps {
  template: Template;
  names?: string;
  date?: string;
  venue?: string;
}

const CARD_SHELL: React.CSSProperties = {
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  boxShadow:
    "0 1px 3px rgba(26,24,20,0.04), 0 24px 48px -24px rgba(26,24,20,0.18)",
  // Enables cqw/cqh inside this card so the overlay scales with the
  // card itself instead of the viewport.
  containerType: "size",
};

export function InvitationCard({ template, names, date, venue }: InvitationCardProps) {
  if (template.cardImage) {
    const overlay = template.cardOverlay ?? "bottom";
    const displayNames = names ?? template.names;
    const displayDate = date ?? template.date;
    const displayVenue = venue ?? template.venue;
    // Soft halo using the template's own paper color makes the text
    // readable on busy areas (w34 starry-night, w36 castle, b04 bouquet)
    // without needing a hard scrim that would dirty the empty cards.
    const textHalo =
      `0 0 1.5cqw ${template.bg}, 0 0 0.6cqw ${template.bg}, 0 0.2cqw 0.4cqw rgba(0,0,0,0.18)`;
    return (
      <div style={{ ...CARD_SHELL, background: template.bg }}>
        <img
          src={template.cardImage}
          alt={template.title}
          loading="lazy"
          decoding="async"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
        {overlay === "bottom" && (
          <div
            style={{
              position: "absolute",
              left: "8%",
              right: "8%",
              bottom: "7%",
              textAlign: "center",
              color: template.ink,
            }}
          >
            <div
              style={{
                fontFamily: template.font,
                fontSize: "8.5cqw",
                lineHeight: 1.05,
                letterSpacing: "-0.01em",
                textShadow: textHalo,
                marginBottom: "2cqw",
              }}
            >
              {displayNames}
            </div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "2.4cqw",
                fontWeight: 700,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: template.accent,
                textShadow: textHalo,
              }}
            >
              {displayDate}
              {displayVenue ? ` · ${displayVenue}` : ""}
            </div>
          </div>
        )}
      </div>
    );
  }
  const ctx: LayoutCtx = {
    bg: template.bg,
    ink: template.ink,
    accent: template.accent,
    font: template.font,
    photo: template.photos[0],
    photos: template.photos,
    names: names ?? template.names,
    date: date ?? template.date,
    venue: venue ?? template.venue,
  };
  const Layout = LAYOUTS[template.layout] ?? LAYOUTS["photo-bleed"];
  return (
    <div style={{ ...CARD_SHELL, background: ctx.bg, color: ctx.ink }}>
      <Layout {...ctx} />
    </div>
  );
}
